import React, { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { Field, FormSpy } from 'react-final-form';

import CheckGroup from 'components/Form/CheckGroup';
import Checkbox from 'components/Form/Checkbox';
import SelectBox from 'components/Form/SelectBox';
import inputField from 'components/Form/inputField';
import { validate as validateForm } from 'components/Form/utils';
import { isRequired, isRequiredWhen } from 'components/Form/validationRules';
import { getSports } from 'api/events';
import listToOptions from 'utils/listToOptions';
import useNetworkState from 'hooks/useNetworkState';

let cachedSports = [];

const NewEventStep1 = () => {
  const [sports, setSports] = useState(cachedSports);
  const { isLoading, setIsLoading, setDoneLoading, setFailedLoading } = useNetworkState();

  const sportOptions = useMemo(
    () => [
      {
        label: 'Selecciona un deporte',
        value: '',
      },
      ...listToOptions(sports),
    ],
    [sports],
  );

  const updateSportsList = useCallback(
    _sports => {
      setSports(_sports);
      cachedSports = sports;
    },
    [sports],
  );

  useEffect(() => {
    setIsLoading();

    getSports()
      .then(response => {
        updateSportsList(response?.body?.data?.sports || []);
        setDoneLoading();
      })
      .catch(() => {
        setFailedLoading();
      });
  }, []); // eslint-disable-line

  return (
    <Fragment>
      <h2 className="text-brandblue text-2xl font-bold mb-4">Paso 1</h2>

      <Field
        component={inputField(CheckGroup)}
        name="event_type"
        inputProps={{
          label: 'Tipo de evento',
          options: [
            { value: 'single_event', label: 'Evento único' },
            { value: 'tournament', label: 'Torneo' },
          ],
          className:
            'appearance-none text-center text-lg border-2 border-solid border-brandblue rounded px-3 py-2 focus:border-teal-600 bg-white focus:outline-none block w-full',
        }}
      />

      <FormSpy
        subscription={{ values: true }}
        render={({ values }) =>
          values?.event_type === 'tournament' && (
            <Fragment>
              <Field
                component={inputField(SelectBox)}
                name="format"
                inputProps={{
                  label: 'Formato del torneo',
                  options: [
                    {
                      label: 'Selecciona un formato',
                      value: '',
                    },
                    {
                      label: 'Un solo grupo',
                      value: 'single_group',
                    },
                    {
                      label: 'Grupos',
                      value: 'groups',
                    },
                  ],
                }}
              />

              <div className="mb-2">
                <Field
                  type="checkbox"
                  component={inputField(Checkbox)}
                  name="round_trip"
                  inputProps={{
                    label: 'Este torneo tendra partidos de ida y vuelta',
                  }}
                />
              </div>
            </Fragment>
          )
        }
      />

      <Field
        component={inputField(CheckGroup)}
        name="registration_type"
        inputProps={{
          label: 'Tipo de inscripción',
          options: [
            { value: 'teams', label: 'Equipos' },
            { value: 'individual', label: 'Individual' },
          ],
        }}
      />

      <Field
        component={inputField(SelectBox)}
        name="sport_id"
        inputProps={{
          label: 'Deporte',
          options: sportOptions,
          disabled: isLoading,
        }}
      />
      <FormSpy
        subscription={{ values: true }}
        render={({ values }) =>
          values?.sport_id === '6' && (
            <Fragment>
              <Field
                component={inputField(SelectBox)}
                name="best_of"
                inputProps={{
                  label: 'Gana juego el mejor de',
                  options: [
                    {
                      label: 'Selecciona una opción',
                      value: '',
                    },
                    {
                      label: 'Mejor de 3 sets',
                      value: 3,
                    },
                    {
                      label: 'Mejor de 5 sets',
                      value: 5,
                    },
                  ],
                }}
              />
            </Fragment>
          )
        }
      />
    </Fragment>
  );
};

export const validations = {
  event_type: [isRequired],
  format: [isRequiredWhen(values => values?.event_type === 'tournament')],
  registration_type: [isRequired],
  sport_id: [isRequired],
  best_of: [isRequiredWhen(values => values?.sport_id === '6')],
};

export const validate = validateForm({ validations, raw: true, messages: true });

export default NewEventStep1;
